// game/player.ts — the Warden (DESIGN §2).
// Move, aim, fire, special, dash with i-frames, quaff Emberburst, take contact
// damage behind a post-hit grace, die.

import type { PlayerState, Vec } from '../types'
import { TUNING } from '../data/tuning'
import { drawSprite } from '../engine/sprites'
import { emit } from '../engine/particles'
import { fx } from '../engine/fx'
import { audio } from '../engine/audio'
import * as Input from '../engine/input'
import { getWeapon } from './weapons'
import type { Run } from './game'
import type { RunModifiers } from './meta'
import type { EnemyEnt } from './entities'

const moveScratch: Vec = { x: 0, y: 0 }
const aimScratch: Vec = { x: 0, y: 0 }
const mouseScratch: Vec = { x: 0, y: 0 }

export class Player implements PlayerState {
  pos: Vec = { x: 0, y: 0 }
  hp: number = TUNING.player.maxHp
  maxHp: number = TUNING.player.maxHp
  gold = 0
  potions = 0
  potionCap: number = TUNING.player.potionCap
  alive = true
  dashCharges: number = TUNING.player.dashCharges
  weaponId = 'cinderblade'
  lampRadius = 0
  speedMul = 1
  zoneSpeedMul = 1
  markedUntil = 0

  // runtime
  radius: number = TUNING.player.radius
  /** external impulse (RunCtx.pushPlayer), decays each step */
  kbx = 0
  kby = 0
  vel: Vec = { x: 0, y: 0 }
  aim: Vec = { x: 1, y: 0 } // normalised facing/aim direction
  facing = 1
  keys = 0
  maxDashCharges: number = TUNING.player.dashCharges
  dashCooldowns: number[] = []
  iframes = 0
  hurtFlash = 0
  primaryCd = 0
  specialCd = 0
  combo = 0
  comboTimer = 0
  muzzle = 0

  // dash / lunge
  dashT = 0
  dashDur = 0
  dashVX = 0
  dashVY = 0
  lungeActive = false
  lungeDamage = 0
  lungeTrailSeconds = 0
  lungeTrailDps = 0
  private lungeHits: EnemyEnt[] = []
  private lungeHitBoss = false
  private trailAcc = 0

  // melee swing visual
  swingT = 0
  swingDur = 0.18
  swingDir = 0
  swingArc = 2.1
  swingRange = 46
  swingSign = 1
  swingHeavy = false

  reset(x: number, y: number, weaponId: string): void {
    this.pos.x = x
    this.pos.y = y
    this.vel.x = 0
    this.vel.y = 0
    this.maxHp = TUNING.player.maxHp
    this.hp = this.maxHp
    this.gold = 0
    this.potions = 0
    this.potionCap = TUNING.player.potionCap
    this.alive = true
    this.weaponId = weaponId
    this.lampRadius = 0
    this.speedMul = 1
    this.zoneSpeedMul = 1
    this.kbx = 0
    this.kby = 0
    this.markedUntil = 0
    this.keys = 0
    this.maxDashCharges = TUNING.player.dashCharges
    this.dashCharges = this.maxDashCharges
    this.dashCooldowns.length = 0
    for (let i = 0; i < this.maxDashCharges; i++) this.dashCooldowns.push(0)
    this.iframes = 0
    this.hurtFlash = 0
    this.primaryCd = 0
    this.specialCd = 0
    this.combo = 0
    this.comboTimer = 0
    this.muzzle = 0
    this.dashT = 0
    this.lungeActive = false
    this.lungeHits.length = 0
    this.swingT = 0
    this.aim.x = 0
    this.aim.y = 1
  }

  /**
   * The meta base layer (DESIGN §7.1). Called once, immediately after reset(),
   * before any relic delta is recomputed — game/relics.ts applies its stat
   * relics as deltas on top of whatever the shrine already paid for.
   */
  applyMeta(m: RunModifiers): void {
    this.maxHp = TUNING.player.maxHp + m.maxHpAdd
    this.hp = this.maxHp
    this.potionCap = TUNING.player.potionCap + m.potionCap
    this.potions = Math.max(0, Math.min(this.potionCap, m.startPotions))
    this.maxDashCharges = Math.max(1, TUNING.player.dashCharges + m.dashChargeAdd)
    this.dashCooldowns.length = 0
    for (let i = 0; i < this.maxDashCharges; i++) this.dashCooldowns.push(0)
    this.dashCharges = this.maxDashCharges
  }

  get spriteId(): string {
    return 'warden_' + this.weaponId
  }

  get invulnerable(): boolean {
    return this.iframes > 0
  }

  // ------------------------------------------------------------------ input

  private readAim(run: Run): void {
    if (Input.usingVirtualAim()) {
      Input.virtualAim(aimScratch)
      const dx = aimScratch.x - this.pos.x
      const dy = aimScratch.y - this.pos.y
      const l = Math.hypot(dx, dy)
      if (l > 0.001) {
        this.aim.x = dx / l
        this.aim.y = dy / l
      }
    } else {
      Input.mouseClient(mouseScratch)
      run.renderer.clientToWorld(mouseScratch.x, mouseScratch.y, aimScratch)
      const dx = aimScratch.x - this.pos.x
      const dy = aimScratch.y - this.pos.y
      const l = Math.hypot(dx, dy)
      if (l > 0.001) {
        this.aim.x = dx / l
        this.aim.y = dy / l
      }
    }
    if (Math.abs(this.aim.x) > 0.15) this.facing = this.aim.x < 0 ? -1 : 1
  }

  // ----------------------------------------------------------------- update

  update(run: Run, dt: number): void {
    if (!this.alive) return
    const weapon = getWeapon(this.weaponId)

    this.readAim(run)

    if (this.iframes > 0) this.iframes -= dt
    if (this.hurtFlash > 0) this.hurtFlash -= dt
    if (this.primaryCd > 0) this.primaryCd -= dt
    if (this.specialCd > 0) this.specialCd -= dt
    if (this.muzzle > 0) this.muzzle -= dt
    if (this.swingT > 0) this.swingT -= dt
    if (this.comboTimer > 0) {
      this.comboTimer -= dt
      if (this.comboTimer <= 0) this.combo = 0
    }
    for (let i = 0; i < this.dashCooldowns.length; i++) {
      if (this.dashCooldowns[i] > 0) {
        this.dashCooldowns[i] -= dt
        if (this.dashCooldowns[i] <= 0) this.dashCharges++
      }
    }
    if (this.dashCharges > this.maxDashCharges) this.dashCharges = this.maxDashCharges

    // --- external impulses (floods, pulls — RunCtx.pushPlayer) decay like
    // enemy knockback and stack onto normal movement, never onto a dash.
    const k = Math.pow(0.0016, dt)
    this.kbx *= k
    this.kby *= k
    if (Math.abs(this.kbx) < 1) this.kbx = 0
    if (Math.abs(this.kby) < 1) this.kby = 0

    // --- dash / lunge motion overrides normal movement
    if (this.dashT > 0) {
      this.updateDash(run, dt)
    } else {
      Input.moveVec(moveScratch)
      const speed = TUNING.player.speed * this.speedMul * this.zoneSpeedMul * weapon.moveMul
      const vx = moveScratch.x * speed
      const vy = moveScratch.y * speed
      this.vel.x = vx
      this.vel.y = vy
      run.world.moveCircle(this.pos, (vx + this.kbx) * dt, (vy + this.kby) * dt, this.radius)

      if (Input.wasPressed('dash') && this.dashCharges > 0) this.startDash(run, moveScratch)
    }

    // --- attacks
    if (this.primaryCd <= 0) {
      const wantFire = weapon.auto ? Input.isDown('fire') : Input.wasPressed('fire')
      if (wantFire) {
        weapon.primary(run, this.aim.x, this.aim.y)
        this.primaryCd = Math.max(this.primaryCd, 0.03)
      }
    }
    if (this.specialCd <= 0 && Input.wasPressed('special')) {
      weapon.special(run, this.aim.x, this.aim.y)
    }
    if (Input.wasPressed('quaff') && this.potions > 0) this.quaff(run)

    // Forms that keep state between inputs — the Stormring's expanding nova and
    // the Warden's Wall — live in weapons.ts and are stepped here. Forms without
    // a tick pay nothing.
    weapon.tick?.(run, dt)
  }

  private startDash(run: Run, dir: Vec): void {
    let dx = dir.x
    let dy = dir.y
    if (dx === 0 && dy === 0) {
      dx = this.aim.x
      dy = this.aim.y
    }
    const l = Math.hypot(dx, dy) || 1
    this.dashVX = (dx / l) * TUNING.player.dashSpeed
    this.dashVY = (dy / l) * TUNING.player.dashSpeed
    this.dashT = TUNING.player.dashSeconds
    this.dashDur = TUNING.player.dashSeconds
    this.iframes = Math.max(this.iframes, TUNING.player.dashIframes)
    this.spendDashCharge()
    this.lungeActive = false
    run.sfx('dash')
    emit(this.pos.x, this.pos.y, {
      count: 8,
      color: '#8fa3c8',
      speed: 60,
      life: 0.25,
      size: 3,
      angle: Math.atan2(-this.dashVY, -this.dashVX),
      spread: 1.1,
    })
  }

  /** Rekindle Lunge (Cinder Blade special) reuses the dash mover. */
  startLunge(run: Run, ax: number, ay: number, length: number, damage: number, trailSeconds: number, trailDps: number): void {
    const l = Math.hypot(ax, ay) || 1
    const dur = 0.18
    this.dashVX = (ax / l) * (length / dur)
    this.dashVY = (ay / l) * (length / dur)
    this.dashT = dur
    this.dashDur = dur
    this.iframes = Math.max(this.iframes, dur + 0.08)
    this.lungeActive = true
    this.lungeDamage = damage
    this.lungeTrailSeconds = trailSeconds
    this.lungeTrailDps = trailDps
    this.lungeHits.length = 0
    this.lungeHitBoss = false
    this.trailAcc = 0
    run.sfx('swing')
    fx.shake(2.5, 0.15)
  }

  private spendDashCharge(): void {
    this.dashCharges = Math.max(0, this.dashCharges - 1)
    for (let i = 0; i < this.dashCooldowns.length; i++) {
      if (this.dashCooldowns[i] <= 0) {
        this.dashCooldowns[i] = TUNING.player.dashRecharge
        return
      }
    }
  }

  private updateDash(run: Run, dt: number): void {
    this.dashT -= dt
    const px = this.pos.x
    const py = this.pos.y
    run.world.moveCircle(this.pos, this.dashVX * dt, this.dashVY * dt, this.radius)
    this.vel.x = this.dashVX
    this.vel.y = this.dashVY

    if (this.lungeActive) {
      // strike everything passed through, once each
      const list = run.enemyPool.live
      for (let i = 0; i < list.length; i++) {
        const e = list[i]
        if (!e.alive) continue
        const d = Math.hypot(e.pos.x - this.pos.x, e.pos.y - this.pos.y)
        if (d > e.radius + this.radius + 12) continue
        if (this.lungeHits.indexOf(e) >= 0) continue
        this.lungeHits.push(e)
        run.damageEnemy(e, this.lungeDamage, this.pos.x, this.pos.y, 200)
      }
      for (let i = 0; i < run.generators.length; i++) {
        const g = run.generators[i]
        if (!g.alive) continue
        const d = Math.hypot(g.pos.x - this.pos.x, g.pos.y - this.pos.y)
        if (d < g.radius + this.radius + 12 && !g.lungeMarked) {
          g.lungeMarked = true
          run.damageGenerator(g, this.lungeDamage)
        }
      }
      if (!this.lungeHitBoss && run.hitBoss(this.pos.x, this.pos.y, this.radius + 12, this.lungeDamage)) {
        this.lungeHitBoss = true
      }
      // burn trail
      this.trailAcc += Math.hypot(this.pos.x - px, this.pos.y - py)
      if (this.trailAcc > 22) {
        this.trailAcc = 0
        run.spawnHazard(this.pos.x, this.pos.y, 24, this.lungeTrailDps, this.lungeTrailSeconds, '#ff8a2a')
      }
      emit(this.pos.x, this.pos.y, {
        count: 2,
        color: '#ffb347',
        speed: 30,
        life: 0.3,
        size: 3,
        shape: 1,
      })
    }

    if (this.dashT <= 0) {
      this.dashT = 0
      if (this.lungeActive) {
        this.lungeActive = false
        for (let i = 0; i < run.generators.length; i++) run.generators[i].lungeMarked = false
        run.spawnHazard(this.pos.x, this.pos.y, 26, this.lungeTrailDps, this.lungeTrailSeconds, '#ff8a2a')
      }
    }
  }

  // ---------------------------------------------------------------- damage

  hurt(run: Run, amount: number, source: string): void {
    if (!this.alive) return
    if (this.iframes > 0) return
    if (amount <= 0) return
    this.hp -= amount
    this.iframes = TUNING.player.contactIframes
    this.hurtFlash = 0.14
    fx.shake(TUNING.fx.shakeHit * 1.4, 0.22)
    fx.hitstop(TUNING.fx.hitstopHit)
    fx.vignette(0.45)
    fx.damageNumber(amount, this.pos.x, this.pos.y - 18, '#ff6b6b')
    audio.sfx('player-hurt')
    emit(this.pos.x, this.pos.y, {
      count: 10,
      color: '#c33',
      speed: 110,
      life: 0.32,
      size: 3,
    })
    if (this.hp <= 0) {
      // Death's Refusal (DESIGN §7.1, row 3): once per run, the descent simply
      // does not end here. Loud on purpose — a second life you cannot miss.
      if (run.reviveCharges > 0) {
        run.reviveCharges--
        this.hp = Math.max(1, Math.round(this.maxHp * run.reviveFraction))
        this.iframes = Math.max(this.iframes, TUNING.player.contactIframes * 3)
        this.hurtFlash = 0.3
        fx.shake(10, 0.8)
        fx.hitstop(0.22)
        fx.vignette(1.2, '#ffb347')
        fx.floatText("DEATH'S REFUSAL", this.pos.x, this.pos.y - 34, '#ffd98a', 17)
        audio.sfx('potion')
        emit(this.pos.x, this.pos.y, {
          count: 46,
          color: '#ffd98a',
          speed: 260,
          speedVar: 180,
          life: 0.9,
          lifeVar: 0.4,
          size: 5,
          shape: 1,
        })
        run.bark('You are refused. Get up.')
        return
      }
      this.hp = 0
      this.alive = false
      run.onPlayerDeath(source)
    }
  }

  heal(n: number): void {
    if (!this.alive) return
    this.hp = Math.min(this.maxHp, this.hp + n)
    fx.floatText('+' + Math.round(n), this.pos.x, this.pos.y - 20, '#8ee87a', 12)
  }

  /** Emberburst — the Gauntlet potion, ours. */
  quaff(run: Run): void {
    if (this.potions <= 0) return
    this.potions--
    const r = TUNING.player.potionRadius
    run.emberburst(this.pos.x, this.pos.y, r, TUNING.player.potionDamage)
    audio.sfx('potion')
    fx.shake(TUNING.fx.shakeExplosion, 0.4)
    fx.hitstop(TUNING.fx.hitstopKill)
    run.bark('Ember answers dark.')
  }

  // ---------------------------------------------------------------- render

  render(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.alive) return
    const bob = Math.sin(time * 8) * (Math.abs(this.vel.x) + Math.abs(this.vel.y) > 12 ? 1 : 0.3)

    // The Warden carries the last ember: a warm pool under the feet makes the
    // player the single most legible body on screen at any zoom.
    const glow = ctx.createRadialGradient(this.pos.x, this.pos.y + 4, 2, this.pos.x, this.pos.y + 4, 34)
    glow.addColorStop(0, 'rgba(255,170,80,0.30)')
    glow.addColorStop(0.5, 'rgba(255,140,60,0.10)')
    glow.addColorStop(1, 'rgba(255,140,60,0)')
    ctx.fillStyle = glow
    ctx.fillRect(this.pos.x - 34, this.pos.y - 30, 68, 68)

    // ground shadow
    ctx.globalAlpha = 0.4
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.ellipse(this.pos.x, this.pos.y + 10, 10, 4, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1

    // melee swing arc
    if (this.swingT > 0) {
      const k = 1 - this.swingT / this.swingDur
      const sweep = this.swingArc
      const a0 = this.swingDir - (sweep / 2) * this.swingSign
      const a1 = a0 + sweep * this.swingSign * Math.min(1, k * 1.35)
      ctx.save()
      ctx.globalAlpha = 0.75 * (1 - k)
      ctx.strokeStyle = this.swingHeavy ? '#ffd27a' : '#ffe9c0'
      ctx.lineWidth = this.swingHeavy ? 8 : 5
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.arc(this.pos.x, this.pos.y, this.swingRange * 0.86, Math.min(a0, a1), Math.max(a0, a1))
      ctx.stroke()
      ctx.globalAlpha = 0.28 * (1 - k)
      ctx.strokeStyle = '#ff8a2a'
      ctx.lineWidth = this.swingHeavy ? 14 : 9
      ctx.stroke()
      ctx.restore()
    }

    const white = this.hurtFlash > 0 ? 0.85 : 0
    drawSprite(ctx, this.spriteId, this.pos.x, this.pos.y - 2 + bob, {
      flip: this.facing < 0,
      white,
      alpha: this.iframes > 0 && this.dashT <= 0 ? 0.72 : 1,
    })

    // muzzle flash
    if (this.muzzle > 0) {
      const k = this.muzzle / 0.07
      ctx.globalAlpha = Math.min(1, k)
      ctx.fillStyle = '#fff3c4'
      ctx.beginPath()
      ctx.arc(this.pos.x + this.aim.x * 16, this.pos.y + this.aim.y * 16, 5 * k + 2, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
    }

    // Form-owned visuals (the nova ring, the raised shield). Drawn last so they
    // sit over the Warden the way a held shield does.
    getWeapon(this.weaponId).render?.(ctx, this.pos.x, this.pos.y)
  }
}
